import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Renders the written analysis Claude produces for a report.
 *
 * It replies in Markdown — bold, headings, lists, and tables comparing
 * contradictory answers. Rendering that as plain text shows the reader the raw
 * "**" and "|" characters, so it is rendered properly here, styled to match
 * the rest of the report rather than left to browser defaults.
 *
 * remark-gfm is what adds tables; without it Markdown tables stay as pipes.
 */
const AnalysisText = ({ children }) => {
  if (!children) return null;

  return (
    <div className="text-[15px] leading-relaxed text-[#1A231C]/90 space-y-3">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => <p className="mb-3 last:mb-0" {...props} />,

          strong: ({ node, ...props }) => (
            <strong className="font-semibold text-[#1A231C]" {...props} />
          ),
          em: ({ node, ...props }) => <em className="italic" {...props} />,

          h1: ({ node, ...props }) => (
            <h4 className="text-[15px] font-semibold text-[#1A231C] mt-5 mb-2" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h4 className="text-[15px] font-semibold text-[#1A231C] mt-5 mb-2" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h4 className="text-[15px] font-semibold text-[#1A231C] mt-5 mb-2" {...props} />
          ),
          h4: ({ node, ...props }) => (
            <h4 className="text-[15px] font-semibold text-[#1A231C] mt-5 mb-2" {...props} />
          ),

          ul: ({ node, ...props }) => (
            <ul className="list-disc pl-5 space-y-1.5 mb-3" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal pl-5 space-y-1.5 mb-3" {...props} />
          ),
          li: ({ node, ...props }) => <li className="pl-1" {...props} />,

          // Tables are how the contradiction analysis is usually written, so
          // they carry real weight here. Scroll rather than squash on narrow
          // screens.
          table: ({ node, ...props }) => (
            <div className="my-4 overflow-x-auto border border-black/10">
              <table className="w-full text-[13.5px] border-collapse" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-brand/5" {...props} />,
          th: ({ node, ...props }) => (
            <th
              className="text-left font-semibold text-[#1A231C] px-3 py-2 border-b border-black/10 align-top"
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td className="px-3 py-2 border-b border-black/5 align-top" {...props} />
          ),

          blockquote: ({ node, ...props }) => (
            <blockquote
              className="border-l-2 border-accent2 pl-4 italic text-[#5C665F] my-3"
              {...props}
            />
          ),
          code: ({ node, ...props }) => (
            <code className="bg-black/5 px-1 py-0.5 text-[13px]" {...props} />
          ),
          hr: () => <hr className="my-4 border-black/10" />,
          a: ({ node, ...props }) => (
            <a className="text-brand underline" target="_blank" rel="noreferrer" {...props} />
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
};

export default AnalysisText;
